import { signal } from '@preact/signals'
import { useEffect } from 'preact/hooks'
import { useLocation } from 'wouter'
import { Button } from '../../components/Button'
import { Card } from '../../components/Card'
import { ErrorBanner } from '../../components/ErrorBanner'
import { Loading } from '../../components/Loading'
import { useAuth } from '../../hooks/useAuth'
import { ROUTES } from '../../routes'
import { styled } from '../../setup'
import type { GuardianLinkResolveResult } from '../../store/auth'
import { borders, colors, spacing, typography } from '../../theme'

const Container = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: ${spacing['2xl']};
`

const ChildInfo = styled('div')`
  text-align: center;
  padding: ${spacing['2xl']};
  background: ${colors.bgSurfaceAlt};
  border-radius: ${borders.radiusLg};
  margin-bottom: ${spacing['2xl']};
`

const ChildLabel = styled('div')`
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: ${spacing.sm};
`

const ChildName = styled('div')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
`

const Description = styled('p')`
  color: ${colors.textSecondary};
  font-size: ${typography.fontSizeBase};
  line-height: 1.6;
  margin: 0 0 ${spacing['2xl']} 0;
  text-align: center;
`

const SelectGroup = styled('div')`
  margin-bottom: ${spacing.lg};
`

const SelectLabel = styled('label')`
  display: block;
  color: ${colors.textMuted};
  font-size: ${typography.fontSizeSm};
  font-weight: ${typography.fontWeightMedium};
  margin-bottom: ${spacing.sm};
`

const Select = styled('select')`
  width: 100%;
  padding: ${spacing.lg};
  background: ${colors.bgSurface};
  border: ${borders.widthMedium} solid ${colors.border};
  border-radius: ${borders.radius};
  color: ${colors.textPrimary};
  font-size: ${typography.fontSizeLg};
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    border-color: ${colors.borderFocus};
    box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.15);
  }
`

const ButtonGroup = styled('div')`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`

const LoginPrompt = styled('div')`
  text-align: center;
`

const LoginTitle = styled('h2')`
  color: ${colors.textPrimary};
  font-size: ${typography.fontSize2xl};
  font-weight: ${typography.fontWeightSemibold};
  margin: 0 0 ${spacing.lg} 0;
`

// Local state
const tokenInfo = signal<GuardianLinkResolveResult | null>(null)
const isResolving = signal(true)
const isProcessing = signal(false)
const selectedKind = signal('PARENT')
const selectedRole = signal('LIMITED_GUARDIAN')
const localError = signal<string | null>(null)

// Relationship types
const GUARDIAN_KINDS = [
  { value: 'PARENT', label: 'Parent' },
  { value: 'STEPPARENT', label: 'Stepparent' },
  { value: 'GUARDIAN', label: 'Legal Guardian' },
  { value: 'SIBLING', label: 'Sibling (18+)' },
  { value: 'OTHER', label: 'Other Trusted Adult' },
]

// Permission roles
const GUARDIAN_ROLES = [
  { value: 'FULL_GUARDIAN', label: 'Full Guardian - Can manage all settings' },
  { value: 'LIMITED_GUARDIAN', label: 'Limited Guardian - Can approve friends and activities' },
  { value: 'VIEW_ONLY', label: 'View Only - Can only see progress' },
]

export function ResolvePage() {
  const {
    isAuthenticated,
    isInitialized,
    login,
    resolveGuardianLinkToken,
    approveGuardianLink,
    denyGuardianLink,
    error,
    clearAuthError,
  } = useAuth()
  const [location, setLocation] = useLocation()

  // Extract token from URL
  const urlParams = new URLSearchParams(location.split('?')[1] || '')
  const token = urlParams.get('token')

  // Resolve token on mount
  useEffect(() => {
    if (!isInitialized.value) return

    // No token - invalid URL
    if (!token) {
      localError.value = 'Invalid link. Please use the link from your email.'
      isResolving.value = false
      return
    }

    // Not authenticated - store token and prompt login
    if (!isAuthenticated.value) {
      sessionStorage.setItem('pendingGuardianToken', token)
      isResolving.value = false
      return
    }

    // Authenticated - resolve the token
    const resolve = async () => {
      isResolving.value = true
      localError.value = null

      try {
        const result = await resolveGuardianLinkToken(token)
        tokenInfo.value = result

        if (result.suggestedKind) {
          selectedKind.value = result.suggestedKind
        }
        if (result.suggestedRole) {
          selectedRole.value = result.suggestedRole
        }
      } catch {
        localError.value = 'Failed to validate link'
      } finally {
        isResolving.value = false
      }
    }

    resolve()
  }, [isInitialized.value, isAuthenticated.value, token])

  // Handle login redirect
  const handleLogin = () => {
    if (token) {
      sessionStorage.setItem('pendingGuardianToken', token)
    }
    login()
  }

  // Handle approve
  const handleApprove = async () => {
    if (!token) return

    isProcessing.value = true
    localError.value = null
    clearAuthError()

    try {
      await approveGuardianLink(token, selectedKind.value, selectedRole.value)
      setLocation(`${ROUTES.GUARDIAN_LINK_COMPLETE}?action=approved`)
    } catch {
      // Error handled by store
    } finally {
      isProcessing.value = false
    }
  }

  // Handle deny
  const handleDeny = async () => {
    if (!token) return

    isProcessing.value = true
    localError.value = null
    clearAuthError()

    try {
      await denyGuardianLink(token)
      setLocation(`${ROUTES.GUARDIAN_LINK_COMPLETE}?action=denied`)
    } catch {
      // Error handled by store
    } finally {
      isProcessing.value = false
    }
  }

  // Loading state
  if (!isInitialized.value || isResolving.value) {
    return <Loading fullScreen message="Validating link..." />
  }

  // Not authenticated - show login prompt
  if (!isAuthenticated.value) {
    return (
      <Container>
        <Card maxWidth="480px">
          <LoginPrompt>
            <LoginTitle>Guardian Link Request</LoginTitle>
            <Description>
              A child has requested to link you as their guardian. Please log in or create an
              account to continue.
            </Description>
            <Button variant="primary" onClick={handleLogin} fullWidth>
              Log In or Sign Up
            </Button>
          </LoginPrompt>
        </Card>
      </Container>
    )
  }

  // Invalid token
  if (localError.value || !tokenInfo.value?.valid) {
    return (
      <Container>
        <Card title="Link Error" maxWidth="480px">
          <ErrorBanner
            message={
              localError.value || tokenInfo.value?.error || 'This link is invalid or has expired.'
            }
          />
          <Button variant="secondary" onClick={() => setLocation(ROUTES.PROFILE)} fullWidth>
            Go to Profile
          </Button>
        </Card>
      </Container>
    )
  }

  // Valid token - show approve/deny UI
  return (
    <Container>
      <Card title="Guardian Link Request" maxWidth="520px">
        {(error.value || localError.value) && (
          <ErrorBanner
            message={error.value || localError.value || ''}
            onDismiss={() => {
              localError.value = null
              clearAuthError()
            }}
          />
        )}

        <ChildInfo>
          <ChildLabel>Child requesting link</ChildLabel>
          <ChildName>{tokenInfo.value.childDisplayName}</ChildName>
        </ChildInfo>

        <Description>
          By approving, you'll be able to manage this child's account, approve their friend
          requests, and monitor their activity.
        </Description>

        <SelectGroup>
          <SelectLabel>Your relationship to this child</SelectLabel>
          <Select
            value={selectedKind.value}
            onChange={(e) => {
              selectedKind.value = (e.target as HTMLSelectElement).value
            }}
          >
            {GUARDIAN_KINDS.map((kind) => (
              <option key={kind.value} value={kind.value}>
                {kind.label}
              </option>
            ))}
          </Select>
        </SelectGroup>

        <SelectGroup>
          <SelectLabel>Your permission level</SelectLabel>
          <Select
            value={selectedRole.value}
            onChange={(e) => {
              selectedRole.value = (e.target as HTMLSelectElement).value
            }}
          >
            {GUARDIAN_ROLES.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
        </SelectGroup>

        <ButtonGroup>
          <Button
            variant="primary"
            onClick={handleApprove}
            loading={isProcessing.value}
            disabled={isProcessing.value}
            fullWidth
          >
            Approve Link
          </Button>
          <Button
            variant="danger"
            onClick={handleDeny}
            loading={isProcessing.value}
            disabled={isProcessing.value}
            fullWidth
          >
            Deny Request
          </Button>
        </ButtonGroup>
      </Card>
    </Container>
  )
}
